/**
 * Aba "Enviar por e-mail" do TaskDetailModal.
 */
import { useMutation } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';

import { apiClient } from '@/lib/api-client';

export interface EmailTabProps {
  taskId: string;
  taskTitle: string;
  /** E-mail do cliente vinculado (pré-preenche o campo "Para") */
  defaultEmail?: string | null;
}

export function EmailTab({ taskId, taskTitle, defaultEmail }: EmailTabProps) {
  const [toEmail, setToEmail] = useState(defaultEmail ?? '');
  const [subject, setSubject] = useState(`Tarefa: ${taskTitle}`);
  const [body, setBody] = useState(
    `Olá,\n\nSegue a tarefa referente a "${taskTitle}".\n\nQualquer dúvida, entre em contato.\n\nAtenciosamente.`,
  );
  const [lastSent, setLastSent] = useState<{ email: string; at: string } | null>(null);

  const sendMut = useMutation({
    mutationFn: () =>
      apiClient.tasksSendEmail(taskId, { toEmail, subject, body }),
    onSuccess: () => {
      setLastSent({ email: toEmail, at: new Date().toLocaleString('pt-BR') });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toEmail.trim() || !subject.trim() || sendMut.isPending) return;
    sendMut.mutate();
  }

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* Para */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--fg-muted)]">Para</label>
          <input
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="email@destinatário.com"
            required
            className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
        </div>

        {/* Assunto */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--fg-muted)]">Assunto</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Assunto do e-mail"
            required
            className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
        </div>

        {/* Corpo */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--fg-muted)]">Mensagem</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Corpo do e-mail…"
            className="w-full resize-y rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
        </div>

        {/* Erro */}
        {sendMut.isError && (
          <p className="text-xs text-red-600">
            Falha ao enviar e-mail. Tente novamente.
          </p>
        )}

        <div className="flex items-center justify-between">
          {lastSent ? (
            <p className="flex items-center gap-1.5 text-xs text-green-600">
              <Mail className="h-3.5 w-3.5" />
              Enviado para {lastSent.email} em {lastSent.at}
            </p>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!toEmail.trim() || !subject.trim() || sendMut.isPending}
            className="rounded-md bg-[var(--accent)] px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40 transition"
          >
            {sendMut.isPending ? 'Enviando…' : 'Enviar e-mail'}
          </button>
        </div>
      </form>
    </div>
  );
}
